<script setup lang="ts">
import { CheckCircleOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons-vue';
import { notification } from 'ant-design-vue';
import { computed, onUnmounted, reactive, ref } from 'vue';
import { useAtenderEncerramentoLegalAi2 } from '~/composables/useAtenderEncerramentoLegalAi2';

interface Props {
  compromissoCodigos?: string[];
  compromisso?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const formRef = ref<any>();

const model = reactive({
  possivelEncerrar: null as boolean | null,
  motivo: null as any,
  dataRegistro: null as any,
  observacao: '',
  justificativa: '',
});

const { status, result, errorMessage, submit, stopPolling, setLoading } = useAtenderEncerramentoLegalAi2();

const isLoading = computed(() => status.value === 'loading');
const isSuccess = computed(() => status.value === 'success');
const isPartial = computed(() => status.value === 'partial');
const showForm = computed(() => !isSuccess.value && !isPartial.value);

const isLote = computed(() => {
  const totalCompromissos = props.compromissoCodigos?.length ?? 0;
  return totalCompromissos > 1;
});

function possivelEncerrarChange() {
  model.motivo = null;
  model.dataRegistro = null;
  model.observacao = '';
  model.justificativa = '';
}

async function handleSubmit() {
  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  setLoading();

  const compromissoCodigos = props.compromissoCodigos?.length
    ? props.compromissoCodigos
    : props.compromisso
      ? [props.compromisso.codigo]
      : [];

  await submit({
    compromissoCodigos,
    possivelEncerrar: model.possivelEncerrar!,
    motivo: model.possivelEncerrar ? model.motivo?.value : undefined,
    dataRegistro: model.possivelEncerrar ? model.dataRegistro : undefined,
    observacao: model.possivelEncerrar ? (model.observacao || undefined) : undefined,
    justificativa: !model.possivelEncerrar ? model.justificativa : undefined,
  });

  if (status.value === 'error') {
    notification.error({
      message: errorMessage.value || 'Tivemos um problema inesperado',
      duration: 10,
    });
  }
}

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <a-form
    v-if="showForm"
    ref="formRef"
    :model="model"
    autocomplete="off"
    layout="vertical"
  >
    <a-alert
      v-if="props.compromissoCodigos && props.compromissoCodigos.length > 1"
      type="warning"
      message="Você irá realizar uma alteração em lote"
      show-icon
      style="margin-bottom: 24px"
    />

    <a-form-item
      label="É possível encerrar essa pasta no Legal AI?"
      name="possivelEncerrar"
      :rules="[{ message: 'Informar se é possível encerrar é obrigatório', required: true }]"
    >
      <a-radio-group
        v-model:value="model.possivelEncerrar"
        @change="possivelEncerrarChange"
      >
        <a-radio-button :value="true">
          Sim
        </a-radio-button>
        <a-radio-button :value="false">
          Não
        </a-radio-button>
      </a-radio-group>
    </a-form-item>

    <!-- Campos para Sim -->
    <div v-if="model.possivelEncerrar === true">
      <a-form-item
        label="Motivo"
        name="motivo"
        :rules="[
          {
            message: 'Motivo é obrigatório',
            required: true,
            trigger: 'change',
            type: 'object',
          },
        ]"
      >
        <RemoteSelect
          v-model:value="model.motivo"
          url="/encerramento-legal-ai-2/motivos"
          placeholder="Selecione o motivo"
          :allow-clear="true"
        />
      </a-form-item>

      <a-form-item
        label="Data do Registro"
        name="dataRegistro"
        :rules="[{ message: 'Data do registro é obrigatória', required: true }]"
      >
        <a-date-picker
          v-model:value="model.dataRegistro"
          format="DD/MM/YYYY"
          value-format="DD/MM/YYYY"
          placeholder="Selecione uma data"
          style="width: 100%"
        />
      </a-form-item>

      <a-form-item
        label="Observação"
        name="observacao"
      >
        <a-textarea v-model:value="model.observacao" :rows="4" />
      </a-form-item>
    </div>

    <!-- Campos para Não -->
    <div v-if="model.possivelEncerrar === false">
      <a-form-item
        label="Justificativa"
        name="justificativa"
        :rules="[{ message: 'Justificativa é obrigatória', required: true }]"
      >
        <a-textarea
          v-model:value="model.justificativa"
          :rows="4"
          placeholder="Explique por que não é possível encerrar"
        />
      </a-form-item>
    </div>

    <a-form-item>
      <a-button
        type="primary"
        :disabled="isLoading"
        :loading="isLoading"
        size="large"
        @click.prevent="handleSubmit"
      >
        <template #icon>
          <SaveOutlined />
        </template>
        {{ isLoading ? 'Salvando...' : 'Salvar' }}
      </a-button>
    </a-form-item>
  </a-form>

  <!-- Resultado de sucesso (simples) -->
  <a-result
    v-if="isSuccess && !isLote"
    status="success"
    title="Atendimento realizado com sucesso"
    sub-title="Agora você pode fechar essa janela"
  >
    <template #extra>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado de sucesso (lote) -->
  <a-result
    v-if="isSuccess && isLote"
    status="success"
    title="Atendimentos realizados com sucesso"
    :sub-title="`${result?.totalSucesso || 0} atendimento(s) processado(s) com sucesso`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 300px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
            </div>
            <CheckCircleOutlined style="color: #52c41a; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>

  <!-- Resultado parcial (lote com falhas) -->
  <a-result
    v-if="isPartial"
    status="warning"
    title="Processamento parcial"
    :sub-title="`${result?.totalSucesso || 0} sucesso(s), ${result?.totalFalha || 0} falha(s)`"
  >
    <template #extra>
      <a-list
        size="small"
        :data-source="result?.resultados || []"
        bordered
        style="margin-bottom: 16px; max-height: 450px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item style="display: flex; justify-content: space-between; align-items: center;">
            <div style="text-align: left; flex: 1;">
              <a :href="`/pasta/${item.codigo}`" target="_blank">
                {{ item.nome || item.codigo }}
              </a>
              <span v-if="!item.sucesso" style="color: #ff4d4f; margin-left: 8px; font-size: 12px;">
                - {{ item.erro }}
              </span>
            </div>
            <CheckCircleOutlined v-if="item.sucesso" style="color: #52c41a; margin-left: 8px;" />
            <ExclamationCircleOutlined v-else style="color: #ff4d4f; margin-left: 8px;" />
          </a-list-item>
        </template>
      </a-list>
      <a-button @click="() => emit('close')">Fechar</a-button>
    </template>
  </a-result>
</template>
